import { debounce } from 'lodash';
import Quill from 'quill';
import { useRef, useEffect, useCallback, useState, useMemo } from 'react';
import styled from 'styled-components';
import { v4 as uuid } from 'uuid';

import { useBreakpoint, zIndices } from '@hivebrite/design-system/Theme';

import {
  getBoundAdditionalValue,
  getLeftPosition,
  getWidthPosition,
} from 'hb-react/shared/components/DesignSystem/QuillEditor/Mentions/utils/position';

import MentionsList from './MentionsList';
import { useQuillEditorContext } from '../contexts/QuillEditorContext';

const Delta = Quill.import('delta');

const overrideQuillEnterBinding = ({ quillRef, overrideEnterBinding }) => {
  quillRef.current.keyboard.addBinding(
    {
      key: 13,
    },
    () => !overrideEnterBinding.current,
  );

  quillRef.current.keyboard.bindings[13].unshift(
    quillRef.current.keyboard.bindings[13].pop(),
  );
};

const handleDocumentKeyDown =
  ({ onKeyDown, onKeyEnter, onKeyEsc, onKeyUp }) =>
  (e) => {
    const { key } = e;

    if (key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();

      onKeyEsc();
    }

    if (key === 'ArrowUp') {
      e.preventDefault();
      e.stopPropagation();

      onKeyUp();
    }

    if (key === 'ArrowDown') {
      e.preventDefault();
      e.stopPropagation();

      onKeyDown();
    }

    if (key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();

      onKeyEnter();
    }
  };

const handleEditorChange = ({
  chars,
  hideMentionsItems,
  quillRef,
  setDisplayMentions,
  setMentionsBoxId,
  maxWords = 2,
  isOpenInModal,
  isMobile,
  setOpacity,
}) => {
  const selection = quillRef.current.getSelection();

  // Don't do anything if we don't have selection from QuillJS
  if (selection === null) {
    return;
  }

  const { index: cursorPos } = selection;
  const textBeforeCursor = quillRef.current.getText(0, cursorPos);

  // This RegExp will match the latest mention that starts from any character
  // of `chars` and consists of `maxWords` words.
  // @example:
  //    Chars: [@], maxWords: 2
  //    rLatestMention === /\B[@](\S+(?:\s[^\s@]*){0,1})$/
  //
  //    "test @john doe".match(rLatestMention);
  //    /* => ['@john doe', 'john doe'] */
  const rLastMention = new RegExp(
    `\\B[${chars.join('')}](\\S+(?:\\s[^\\s${chars.join('')}]*){0,${Math.max(
      0,
      maxWords - 1,
    )}})$`,
  );

  const lastMentionMatch = textBeforeCursor.match(rLastMention);

  if (lastMentionMatch) {
    const search = lastMentionMatch[1];

    const {
      height: heightCursor,
      left: leftCursor,
      top: topCursor,
    } = quillRef.current.getBounds(cursorPos);
    const { left: leftQuill, top: topQuill } =
      quillRef.current.root.getBoundingClientRect();

    setDisplayMentions({
      bounds: {
        left: getLeftPosition({
          leftQuill,
          leftCursor,
          isOpenInModal,
          isMobile,
        }),
        top: `${
          topCursor +
          heightCursor +
          getBoundAdditionalValue(topQuill, isOpenInModal)
        }px`,
        width: getWidthPosition({ isOpenInModal, isMobile }),
      },
      char: chars.find((char) => lastMentionMatch[0].startsWith(char)),
      search,
    });
    setOpacity(0);
    setMentionsBoxId();
  } else {
    hideMentionsItems();
  }
};

const Mentions = ({ id = undefined }) => {
  const isOpenInModal =
    !!document.querySelector('[data-id="design-system-modal"]') ||
    !!document.querySelector('dialog[open]');
  const {
    isEditorFocus,
    offEditorChange,
    onEditorChange,
    quillRef,
    options: {
      toolbar: {
        mentions: {
          chars,
          getOptionHtmlId,
          fetchItems,
          linkRenderer,
          itemRenderer,
        },
      },
    },
    setMentionsBoxId,
    setActiveMentionOptionId,
  } = useQuillEditorContext();
  const breakpoint = useBreakpoint();
  const isMobile = breakpoint === 's';

  const overrideEnterBinding = useRef(false);
  const mentionListRef = useRef();

  const [displayMentions, setDisplayMentions] = useState(null);
  const [mentionActiveIndex, setMentionActiveIndex] = useState(0);
  const [hoverList, setHoverList] = useState(false);
  const [mentionsItems, setMentionsItems] = useState([]);
  const [opacity, setOpacity] = useState(0); // Opacity is used to hide the dropdown before we're sure it's in the right position
  const wrapperId = useMemo(() => id || uuid(), [id]);

  // Flag to mark the process of closing the Mention dropdown on Escape button
  const closingOnEsc = useRef(false);

  const handleClickMention = useCallback(
    ({ mention, search, char }) => {
      // Force focus for get selection
      quillRef.current.focus();

      const { index: cursorPos } = quillRef.current.getSelection();

      const index = cursorPos - search.length - 1;
      const length = search.length + 1;
      const { id, display } = linkRenderer(mention, char);

      quillRef.current.deleteText(index, length, 'user');
      quillRef.current.insertText(index, display, 'mention', id);

      // We add an empty space after the mention
      const delta = new Delta().retain(index + display.length).insert(' ');

      quillRef.current.updateContents(delta);
      quillRef.current.setSelection(index + display.length + 1, 0);
    },
    [linkRenderer],
  );

  const hideMentionsItems = useCallback(() => {
    setDisplayMentions(null);
    setMentionActiveIndex(0);
    setOpacity(0);
    setMentionsBoxId(null);
    setActiveMentionOptionId(null);
  }, []);
  const handleMouseEnterList = useCallback(() => setHoverList(true), []);
  const handleMouseLeaveList = useCallback(() => setHoverList(false), []);

  const handleFetchItems = useCallback(
    debounce(
      (search) => fetchItems(search).then((items) => setMentionsItems(items)),
      250,
    ),
    [fetchItems],
  );

  // DidMount
  useEffect(() => {
    if (quillRef.current) {
      // Override default Enter binding from QuillJS
      overrideQuillEnterBinding({
        quillRef,
        overrideEnterBinding,
      });
    }
  }, [quillRef.current]);

  // Override only if mentions are display
  useEffect(() => {
    overrideEnterBinding.current = displayMentions && mentionsItems.length > 0;
  }, [displayMentions, mentionsItems]);

  // Prevent key press up, down, enter when mentions are display
  useEffect(() => {
    const handleKeyDown = handleDocumentKeyDown({
      onKeyEsc: () => {
        closingOnEsc.current = true;
        hideMentionsItems();
      },
      onKeyUp: () => {
        let nextIndex = mentionActiveIndex - 1;

        nextIndex = nextIndex < 0 ? mentionsItems.length - 1 : nextIndex;

        setMentionActiveIndex(nextIndex);
        setActiveMentionOptionId(getOptionHtmlId(nextIndex));
      },
      onKeyDown: () => {
        let nextIndex = mentionActiveIndex + 1;

        nextIndex = nextIndex > mentionsItems.length - 1 ? 0 : nextIndex;

        setMentionActiveIndex(nextIndex);
        setActiveMentionOptionId(getOptionHtmlId(nextIndex));
      },
      onKeyEnter: () => {
        handleClickMention({
          mention: mentionsItems[mentionActiveIndex],
          search: displayMentions.search,
          char: displayMentions.char,
        });
      },
    });

    const handleKeyUp = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();

        // Remove the listener, because it was skipped in componentWillUnmount
        quillRef.current?.root?.removeEventListener('keyup', handleKeyUp);
        closingOnEsc.current = false;
      }
    };

    if (displayMentions && mentionsItems.length > 0) {
      // Bind to ContentEditable to be sure in correct even handlers sequence
      quillRef.current?.root?.addEventListener('keydown', handleKeyDown);
      quillRef.current?.root?.addEventListener('keyup', handleKeyUp);
    }

    return () => {
      quillRef.current?.root?.removeEventListener('keydown', handleKeyDown);
      if (!closingOnEsc.current) {
        quillRef.current?.root?.removeEventListener('keyup', handleKeyUp);
      }
    };
  }, [mentionsItems, mentionActiveIndex, displayMentions]);

  // Set event for QuillJS
  useEffect(() => {
    const handleChange = () =>
      handleEditorChange({
        chars,
        isOpenInModal,
        hideMentionsItems,
        quillRef,
        setDisplayMentions,
        setMentionsBoxId: () => {
          setMentionsBoxId(wrapperId);
          setActiveMentionOptionId(getOptionHtmlId(mentionActiveIndex));
        },
        isMobile,
        setOpacity,
      });

    onEditorChange(handleChange);

    return () => offEditorChange(handleChange);
  }, [chars, onEditorChange, offEditorChange]);

  // Fetching mentions items
  useEffect(() => {
    if (displayMentions) {
      handleFetchItems(displayMentions.search);
    } else {
      // Reset items if search is empty
      setMentionsItems([]);
    }
  }, [handleFetchItems, displayMentions?.search]);

  // Hide mentions if we lose editor focus
  useEffect(() => {
    if (!hoverList && !isEditorFocus) {
      hideMentionsItems();
    }
  }, [hoverList, isEditorFocus]);

  // Check if the mentions list is out of the screen
  // and move it to the left if it is
  useEffect(() => {
    if (!isMobile && mentionsItems?.length && displayMentions?.bounds) {
      const rect = mentionListRef.current?.getBoundingClientRect();
      const mentionsListWidth = rect?.width;
      const mentionsListRight = rect?.right;
      const windowWidth = window.innerWidth;
      const { index: cursorPos } = quillRef.current.getSelection();

      const { left: leftCursor } = quillRef.current.getBounds(cursorPos);

      if (mentionsListRight > windowWidth) {
        const newLeftPositionOutOfModal =
          leftCursor - mentionsListWidth > 0
            ? leftCursor - mentionsListWidth
            : 0;

        setDisplayMentions({
          ...displayMentions,
          bounds: {
            ...displayMentions.bounds,
            width: 'auto',
            left: isOpenInModal
              ? `${windowWidth - mentionsListWidth}px` // position value is fixed so we calculate based on window width
              : `${newLeftPositionOutOfModal}px`, // position is absolute so we calculate based on cursor position
          },
        });
      }
    }
    setOpacity(1);
  }, [mentionsItems, quillRef, isMobile]);

  return (
    <Wrapper
      $isOpenInModal={isOpenInModal}
      data-testid="user-mentions-modal"
      id={wrapperId}
      style={{
        transform: `translate3d(${displayMentions?.bounds.left}, ${displayMentions?.bounds.top}, 0)`,
        opacity,
        width: displayMentions?.bounds?.width,
      }}
    >
      {displayMentions && (
        <MentionsList
          ref={mentionListRef}
          char={displayMentions?.char}
          itemRenderer={itemRenderer}
          mentionActiveIndex={mentionActiveIndex}
          mentions={mentionsItems}
          onClickMention={handleClickMention}
          onMouseEnter={handleMouseEnterList}
          onMouseLeave={handleMouseLeaveList}
          search={displayMentions?.search}
          getOptionHtmlId={getOptionHtmlId}
        />
      )}
    </Wrapper>
  );
};

export default Mentions;

const Wrapper = styled.div`
  position: ${({ $isOpenInModal }) => ($isOpenInModal ? 'fixed' : 'absolute')};
  left: 0;
  top: 0;
  z-index: ${zIndices.popover};
`;
